import React from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import styles from './AIAgentOrb.module.scss'

export type AIAgentStatus = 'idle' | 'thinking' | 'responding' | 'error' | 'offline'

interface AIAgentOrbProps {
  status: AIAgentStatus
  onClick?: () => void
  className?: string
}

const AIAgentOrb: React.FC<AIAgentOrbProps> = ({ status, onClick, className }) => {
  const shouldReduceMotion = useReducedMotion()

  const getAriaLabel = (): string => {
    switch (status) {
      case 'idle':
        return 'Asistente con IA - Listo'
      case 'thinking':
        return 'Asistente con IA - Pensando'
      case 'responding':
        return 'Asistente con IA - Respondiendo'
      case 'error':
        return 'Asistente con IA - Error'
      case 'offline':
        return 'Asistente con IA - Sin conexión'
      default:
        return 'Asistente con IA'
    }
  }

  const shouldAnimate = !shouldReduceMotion
  const isAnimating = status === 'thinking' || status === 'responding'

  return (
    <div
      className={`${styles.orbContainer} ${className || ''}`}
    >
      <motion.div
        className={styles.glowHalo}
        animate={
          shouldAnimate && isAnimating
            ? {
                opacity: [0.3, 0.6, 0.3],
                scale: [1, 1.1, 1],
              }
            : { opacity: 0.2, scale: 1 }
        }
        transition={{
          duration: status === 'thinking' ? 2 : 1.5,
          repeat: isAnimating ? Infinity : 0,
          ease: 'easeInOut',
        }}
      />

      <motion.button
        className={`${styles.sphere} ${styles[`sphere_${status}`]}`}
        onClick={onClick}
        aria-label={getAriaLabel()}
        initial={shouldAnimate ? { scale: 0, opacity: 0 } : false}
        animate={
          shouldAnimate && isAnimating
            ? {
                scale: [1, status === 'thinking' ? 1.04 : 1.02, 1],
                opacity: 1,
              }
            : shouldAnimate
              ? { scale: 1, opacity: 1 }
              : { scale: 1, opacity: 1 }
        }
        transition={
          shouldAnimate
            ? isAnimating
              ? {
                  scale: {
                    duration: status === 'thinking' ? 2 : 1.5,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  },
                  opacity: {
                    duration: 0.6,
                  },
                }
              : {
                  scale: {
                    type: 'spring',
                    stiffness: 200,
                    damping: 15,
                  },
                  opacity: {
                    duration: 0.6,
                  },
                }
            : {}
        }
        whileHover={shouldAnimate && status !== 'offline' ? { scale: 1.02 } : {}}
        whileTap={status !== 'offline' ? { scale: 0.98 } : {}}
        disabled={status === 'offline'}
      >
        <div className={styles.sphereSurface}>
          <div className={styles.sphereGradient} />
          <div className={styles.highlightMain} />
          <div className={styles.highlightSecondary} />
          <div className={styles.shadowDark} />
        </div>
      </motion.button>

    </div>
  )
}

export default AIAgentOrb
